import { InsightDirection } from '@api/api/insights';
import {
  KgInsightFilterV2Request,
  KgInsightFilterV2Response,
  kgInsights,
} from '@api/api/kg-insights';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useInfiniteQuery, useQueries } from '@tanstack/react-query';
import { fetchSnakeByName } from '@terminal/services/overview-widget/overview-widget-service';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import {
  PulseInsightCategory,
  PulseSentimentDirection,
  PulseSentimentInsight,
  PulseSubject,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { MENTIONS_WINDOW_DAYS } from '@terminal/views/theme-overview/tab-content/pulse-overview/services/theme-mentions-service';
import {
  toPulseInsight,
  toPulseScenarioInsight,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/utils/toPulseInsight';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useFilteredInsights } from '../insights/useFilteredInsights';

const QUERY_KEY_PULSE_SENTIMENT_INSIGHTS = 'QUERY_KEY_PULSE_SENTIMENT_INSIGHTS';
const QUERY_KEY_PULSE_SCENARIO_CUMULATIVE_RETURN =
  'QUERY_KEY_PULSE_SCENARIO_CUMULATIVE_RETURN';
const INSIGHTS_WINDOW_DAYS = 30;
const DEFAULT_PAGE_SIZE = 20;

type KgPulseInsightCategory = Exclude<PulseInsightCategory, 'scenario_insight'>;

const KG_CATEGORY_ORDER: KgPulseInsightCategory[] = [
  'market_catalyst',
  'company_catalyst',
  'earnings_recap',
];

export const CATEGORY_ORDER: PulseInsightCategory[] = [
  ...KG_CATEGORY_ORDER,
  'scenario_insight',
];

interface CategoryCount {
  count: number;
  hasMore: boolean;
}

interface CategoryFeed {
  insights: PulseSentimentInsight[];
  hasMore: boolean;
}

export const mergePinnedInsights = ({
  pinnedInsights,
  feed,
  category = null,
}: {
  pinnedInsights: PulseSentimentInsight[];
  feed: PulseSentimentInsight[];
  category?: PulseInsightCategory | null;
}): PulseSentimentInsight[] =>
  [
    ...new Map(
      [...pinnedInsights, ...feed].map(insight => [insight.id, insight])
    ).values(),
  ].filter(insight => !category || insight.category === category);

export const deriveCategoryCounts = ({
  pinnedInsights,
  allInsights,
  allHasMore,
  fetched,
}: {
  pinnedInsights: PulseSentimentInsight[];
  allInsights: PulseSentimentInsight[];
  allHasMore: boolean;
  fetched: Partial<Record<PulseInsightCategory, CategoryFeed>>;
}): Record<PulseInsightCategory, CategoryCount> =>
  Object.fromEntries(
    CATEGORY_ORDER.map(category => {
      const { insights, hasMore } = fetched[category] ?? {
        insights: allInsights,
        hasMore: allHasMore,
      };

      return [
        category,
        {
          count: mergePinnedInsights({
            pinnedInsights,
            feed: insights,
            category,
          }).length,
          hasMore,
        },
      ];
    })
  ) as Record<PulseInsightCategory, CategoryCount>;

const insightSubjects = (subject: PulseSubject, themeIds?: string[]) =>
  subject.type === 'entity'
    ? [{ type: 'entity' as const, id: subject.item.tag }]
    : (themeIds ?? [subject.item.id]).map(id => ({
        type: 'theme' as const,
        id,
      }));

const useInsightsQuery = ({
  subjects,
  companyIds,
  direction,
  pageSize,
  category,
  enabled,
}: {
  subjects: { type: 'theme' | 'entity'; id: string }[];
  companyIds?: string[];
  direction: PulseSentimentDirection;
  pageSize: number;
  category: KgPulseInsightCategory | null;
  enabled: boolean;
}) =>
  useInfiniteQuery({
    queryKey: [
      QUERY_KEY_PULSE_SENTIMENT_INSIGHTS,
      subjects,
      companyIds,
      direction,
      pageSize,
      category,
    ],
    queryFn: async ({ signal, pageParam }) => {
      const body: KgInsightFilterV2Request = {
        subjects,
        subject_scope: 'mentions',
        ...(direction !== 'all' && {
          sentiments: [direction === 'tailwind' ? 'positive' : 'negative'],
        }),
        from: startOfDay(
          subDays(new Date(), INSIGHTS_WINDOW_DAYS)
        ).toISOString(),
        to: endOfDay(new Date()).toISOString(),
        page: pageParam,
        page_size: pageSize,
        categories: category ? [category] : KG_CATEGORY_ORDER,
      };

      const summaries = await wretchRequest<KgInsightFilterV2Response>(
        kgInsights.postKgInsightFilterV2.path,
        {
          method: 'post',
          body,
          schema: kgInsights.postKgInsightFilterV2.schema,
          signal,
        }
      );

      const allowedCompanies = companyIds ? new Set(companyIds) : undefined;
      const filteredSummaries = allowedCompanies
        ? summaries.filter(summary =>
            summary.mentions?.some(
              mention =>
                mention.ref_type === 'entity' &&
                allowedCompanies.has(mention.ref_id)
            )
          )
        : summaries;

      return {
        insights: [
          ...new Map(
            filteredSummaries.map(summary => [
              summary.id,
              toPulseInsight(summary, direction),
            ])
          ).values(),
        ],
        received: summaries.length,
      };
    },
    enabled: enabled && subjects.length > 0 && companyIds?.length !== 0,
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.received === pageSize ? lastPageParam + 1 : undefined,
  });

export const usePulseSentimentInsights = ({
  subject,
  themeIds,
  companyIds,
  direction,
  category = null,
  enabled = true,
  pageSize = DEFAULT_PAGE_SIZE,
  pinnedInsights = [],
}: {
  subject: PulseSubject;
  themeIds?: string[];
  companyIds?: string[];
  direction: PulseSentimentDirection;
  category?: PulseInsightCategory | null;
  enabled?: boolean;
  pageSize?: number;
  pinnedInsights?: PulseSentimentInsight[];
}) => {
  const subjects = insightSubjects(subject, themeIds);
  const mentionCompanyIds = subject.type === 'theme' ? companyIds : undefined;
  const subjectId =
    subject.type === 'theme' ? subject.item.id : subject.item.tag;
  const isEntity = subject.type === 'entity';
  const shared = {
    subjects,
    companyIds: mentionCompanyIds,
    direction,
    pageSize,
  };

  const allQuery = useInsightsQuery({ ...shared, category: null, enabled });
  const categoryQueries: Record<
    KgPulseInsightCategory,
    ReturnType<typeof useInsightsQuery>
  > = {
    market_catalyst: useInsightsQuery({
      ...shared,
      category: 'market_catalyst',
      enabled: enabled && category === 'market_catalyst',
    }),
    company_catalyst: useInsightsQuery({
      ...shared,
      category: 'company_catalyst',
      enabled: enabled && category === 'company_catalyst',
    }),
    earnings_recap: useInsightsQuery({
      ...shared,
      category: 'earnings_recap',
      enabled: enabled && category === 'earnings_recap',
    }),
  };
  const scenarioInsightsQuery = useFilteredInsights(
    {
      entity: enabled && isEntity ? [subjectId] : [],
      from_date: startOfDay(subDays(new Date(), MENTIONS_WINDOW_DAYS)),
      to_date: endOfDay(new Date()),
    },
    100
  );
  const scenarioInsightResults = scenarioInsightsQuery.data?.data?.result ?? [];
  const scenarioInsightsForCumulativeReturn = [
    ...new Map([
      ...scenarioInsightResults.map(
        insight => [insight.id, insight.created_at] as const
      ),
      ...pinnedInsights
        .filter(insight => insight.category === 'scenario_insight')
        .map(insight => [insight.id, insight.date] as const),
    ]),
  ];
  const cumulativeReturnQueries = useQueries({
    queries: scenarioInsightsForCumulativeReturn.map(
      ([insightId, createdAt]) => {
        const formattedFromDate = formatInTimeZone(
          createdAt,
          'UTC',
          'yyyyMMdd'
        );

        return {
          queryKey: [
            QUERY_KEY_PULSE_SCENARIO_CUMULATIVE_RETURN,
            subjectId,
            insightId,
            formattedFromDate,
          ],
          queryFn: () =>
            fetchSnakeByName(
              `${subjectId}.price.cumulative_returns(date=${formattedFromDate})`
            ),
          enabled: enabled && isEntity,
          staleTime: getTimeTillEndOfDayMs(),
          select: (response: Awaited<ReturnType<typeof fetchSnakeByName>>) =>
            response.data?.result.data.map(({ value }) => value * 100).at(-1),
        };
      }
    ),
  });
  const cumulativeReturnsByInsightId = new Map(
    scenarioInsightsForCumulativeReturn.map(
      ([insightId], index) =>
        [insightId, cumulativeReturnQueries[index]?.data] as const
    )
  );
  const enrichedPinnedInsights = pinnedInsights.map(insight =>
    insight.category === 'scenario_insight'
      ? {
          ...insight,
          reactionPercent:
            cumulativeReturnsByInsightId.get(insight.id) ??
            insight.reactionPercent,
        }
      : insight
  );

  const scenarioDirection =
    direction === 'tailwind'
      ? InsightDirection.Bullish
      : InsightDirection.Bearish;

  const scenarioInsights = scenarioInsightResults
    .filter(
      insight =>
        direction === 'all' || insight.metadata.direction === scenarioDirection
    )
    .map(insight =>
      toPulseScenarioInsight(
        insight,
        cumulativeReturnsByInsightId.get(insight.id)
      )
    );

  const allInsights = [
    ...(allQuery.data?.pages.flatMap(page => page.insights) ?? []),
    ...scenarioInsights,
  ].sort(
    (first, second) =>
      new Date(second.date).getTime() - new Date(first.date).getTime()
  );
  const fetched: Partial<Record<PulseInsightCategory, CategoryFeed>> = {
    scenario_insight: {
      insights: scenarioInsights,
      hasMore: false,
    },
  };

  for (const [key, query] of Object.entries(categoryQueries)) {
    if (query.data) {
      fetched[key as KgPulseInsightCategory] = {
        insights: query.data.pages.flatMap(page => page.insights),
        hasMore: query.hasNextPage,
      };
    }
  }

  const isScenarioCategory = category === 'scenario_insight';
  const activeQuery =
    category && !isScenarioCategory ? categoryQueries[category] : allQuery;
  const activeQueryState = isScenarioCategory
    ? {
        isLoading: scenarioInsightsQuery.isLoading,
        isError: scenarioInsightsQuery.isError,
        hasNextPage: false,
        isFetchingNextPage: false,
      }
    : activeQuery;
  const activeInsights = category
    ? (fetched[category]?.insights ?? [])
    : allInsights;

  return {
    data: mergePinnedInsights({
      pinnedInsights: enrichedPinnedInsights,
      feed: activeInsights,
      category,
    }),
    counts: deriveCategoryCounts({
      pinnedInsights: enrichedPinnedInsights,
      allInsights,
      allHasMore: allQuery.hasNextPage,
      fetched,
    }),
    pinnedInsights: enrichedPinnedInsights,
    isLoading:
      activeQueryState.isLoading ||
      (isEntity && category === null && scenarioInsightsQuery.isLoading),
    isError:
      activeQueryState.isError ||
      (isEntity && category === null && scenarioInsightsQuery.isError),
    fetchNextPage: activeQuery.fetchNextPage,
    hasNextPage: activeQueryState.hasNextPage,
    isFetchingNextPage: activeQueryState.isFetchingNextPage,
  };
};
